import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { useConsultation } from "../context/ConsultationContext";

const MAINLAND = [
  "Direct trade with the entire UAE & government contracts",
  "Physical office mandatory (LLC structure)",
  "100% foreign ownership for most activities",
  "Best for: retail, construction, local services, SMEs",
];

const FREEZONE = [
  "Focused on international trade & designated zones",
  "Flexible desk, virtual or physical office",
  "0% corporate tax on qualifying income · 0% personal tax",
  "Best for: tech, consultants, e-commerce, global trade",
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.08 },
  }),
};

export default function MainlandFreezone() {
  const { open } = useConsultation();

  return (
    <section
      id="mainland-freezone"
      data-testid="mainland-freezone-section"
      className="relative bg-[#F8FAFC] py-20 md:py-32"
    >
      <div className="container-x">
        <div className="max-w-3xl">
          <p className="overline text-[#002366]/70">The Choice</p>
          <div className="gold-rule mt-5 mb-6" />
          <h2 className="font-heading font-light tracking-tight text-[#0F172A] text-3xl sm:text-4xl lg:text-5xl leading-tight">
            Where does your business{" "}
            <span className="font-black text-[#002366]">belong</span>?
          </h2>
          <p className="mt-5 text-slate-600 text-base md:text-lg leading-relaxed max-w-2xl">
            Two distinct routes. Two different mandates. StartINC helps you choose the
            structure that maximises your ownership, tax position and market access.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Mainland — dark card */}
          <motion.div
            data-testid="mainland-card"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            custom={0}
            className="lg:col-span-7 relative overflow-hidden rounded-sm bg-[#002366] text-white p-8 md:p-12 min-h-[420px] flex flex-col"
          >
            <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#3366FF]/10 blur-3xl" />
            <div className="flex items-start justify-between gap-6 relative">
              <div>
                <p className="overline text-[#3366FF]">Column A · The Unlimited Gateway</p>
                <h3 className="mt-4 font-heading font-light text-3xl md:text-4xl tracking-tight leading-tight">
                  Mainland <span className="font-black">Setup</span>
                </h3>
              </div>
              <span className="text-[#3366FF] font-heading font-black text-5xl md:text-6xl leading-none opacity-40">
                01
              </span>
            </div>

            <ul className="mt-8 space-y-3 relative">
              {MAINLAND.map((t) => (
                <li key={t} className="flex gap-3 text-slate-200/90 text-sm md:text-base">
                  <Check className="w-5 h-5 shrink-0 text-[#3366FF] mt-0.5" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>

            <div className="mt-auto pt-10 flex flex-wrap gap-3 relative">
              <Link
                data-testid="mainland-learn-more"
                to="/company-formation/mainland"
                className="btn-outline-gold text-sm"
              >
                Explore Mainland <ArrowUpRight className="w-4 h-4" />
              </Link>
              <button
                data-testid="mainland-consult"
                onClick={() => open("mainland")}
                className="inline-flex items-center gap-2 text-sm font-heading text-white/80 hover:text-white"
              >
                Talk to a Mainland advisor <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>

          {/* Freezone — light card */}
          <motion.div
            data-testid="freezone-card"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            custom={1}
            className="lg:col-span-5 relative overflow-hidden rounded-sm bg-white border border-slate-200 p-8 md:p-12 min-h-[420px] flex flex-col"
          >
            <div className="flex items-start justify-between gap-6">
              <div>
                <p className="overline text-[#002366]/70">Column B · Tax-Efficient Hub</p>
                <h3 className="mt-4 font-heading font-light text-3xl md:text-4xl tracking-tight leading-tight text-[#0F172A]">
                  Freezone <span className="font-black text-[#002366]">Setup</span>
                </h3>
              </div>
              <span className="text-[#002366] font-heading font-black text-5xl md:text-6xl leading-none opacity-15">
                02
              </span>
            </div>

            <ul className="mt-8 space-y-3">
              {FREEZONE.map((t) => (
                <li key={t} className="flex gap-3 text-slate-700 text-sm md:text-base">
                  <Check className="w-5 h-5 shrink-0 text-[#002366] mt-0.5" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>

            <div className="mt-auto pt-10 flex flex-wrap gap-3">
              <Link
                data-testid="freezone-learn-more"
                to="/company-formation/freezone"
                className="btn-navy text-sm"
              >
                Explore Freezone <ArrowUpRight className="w-4 h-4" />
              </Link>
              <button
                data-testid="freezone-consult"
                onClick={() => open("freezone")}
                className="inline-flex items-center gap-2 text-sm font-heading text-[#002366] hover:text-[#001A4D]"
              >
                Talk to a Freezone advisor <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        </div>

        <div className="mt-12 flex items-center gap-4">
          <div className="h-px flex-1 bg-slate-300/80" />
          <button
            data-testid="take-setup-quiz"
            onClick={() => open("other")}
            className="btn-navy"
          >
            Not sure? Take the Setup Quiz
          </button>
          <div className="h-px flex-1 bg-slate-300/80" />
        </div>
      </div>
    </section>
  );
}
